import { memo, useState } from 'react';
import { brandColors, palette, rgba } from '@aegislab/design-tokens';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { reducedMotion as reducedMotionQuery } from '@/shared/ui/theme';
import * as s from './NeuralPath.css';

// -- Types --

type LoginStep = 'name' | 'method' | 'otp' | 'password' | 'success';
type NodeState = 'completed' | 'current' | 'future' | 'inactive';
type EdgeState = 'active' | 'inactive' | 'dim';

export interface NeuralPathProps {
  currentStep: LoginStep;
  phase: number;
  sceneExiting?: boolean;
}

// -- Layout --

const NODES = [
  { id: 'ident', cx: 48, cy: 40, label: 'IDENT', labelDy: 24 },
  { id: 'auth', cx: 160, cy: 40, label: 'AUTH', labelDy: 24 },
  { id: 'otp', cx: 290, cy: 16, label: 'OTP', labelDy: -8 },
  { id: 'pwd', cx: 290, cy: 64, label: 'PWD', labelDy: 18 },
  { id: 'link', cx: 432, cy: 40, label: 'LINK', labelDy: 24 },
] as const;

const EDGES = [
  { id: 'e1', d: 'M 48 40 L 160 40', from: 'ident', to: 'auth' },
  { id: 'e2a', d: 'M 160 40 C 210 40, 250 16, 290 16', from: 'auth', to: 'otp' },
  { id: 'e2b', d: 'M 160 40 C 210 40, 250 64, 290 64', from: 'auth', to: 'pwd' },
  { id: 'e3a', d: 'M 290 16 C 330 16, 392 40, 432 40', from: 'otp', to: 'link' },
  { id: 'e3b', d: 'M 290 64 C 330 64, 392 40, 432 40', from: 'pwd', to: 'link' },
  { id: 'e-passkey', d: 'M 160 40 L 432 40', from: 'auth', to: 'link' },
] as const;

// -- Colors (literal for SVG — no CSS vars) --

const CYAN = brandColors.secondary.default;
const CYAN_GLOW = rgba(palette.cyan, 0.5);
const CYAN_DIM = rgba(palette.cyan, 0.2);
const CYAN_GHOST = rgba(palette.cyan, 0.08);

// -- State logic --

function getNodeStates(
  step: LoginStep,
  branch: 'otp' | 'password' | 'passkey' | null
): Record<string, NodeState> {
  switch (step) {
    case 'name':
      return { ident: 'current', auth: 'future', otp: 'future', pwd: 'future', link: 'future' };
    case 'method':
      return { ident: 'completed', auth: 'current', otp: 'future', pwd: 'future', link: 'future' };
    case 'otp':
      return {
        ident: 'completed',
        auth: 'completed',
        otp: 'current',
        pwd: 'inactive',
        link: 'future',
      };
    case 'password':
      return {
        ident: 'completed',
        auth: 'completed',
        otp: 'inactive',
        pwd: 'current',
        link: 'future',
      };
    case 'success':
      return {
        ident: 'completed',
        auth: 'completed',
        otp: branch === 'otp' ? 'completed' : 'inactive',
        pwd: branch === 'password' ? 'completed' : 'inactive',
        link: 'current',
      };
    default:
      return { ident: 'future', auth: 'future', otp: 'future', pwd: 'future', link: 'future' };
  }
}

function getEdgeState(
  id: string,
  from: string,
  to: string,
  nodeStates: Record<string, NodeState>,
  branch: 'otp' | 'password' | 'passkey' | null
): EdgeState {
  // Passkey direct edge: dim by default, active on passkey success
  if (id === 'e-passkey') {
    if (branch === 'passkey') {
      const f = nodeStates[from];
      const t = nodeStates[to];
      if (f === 'completed' && (t === 'completed' || t === 'current')) return 'active';
    }
    // OTP/PWD 분기 선택 시 숨김, 그 외엔 dim으로 경로 표시
    if (branch === 'otp' || branch === 'password') return 'inactive';
    return 'dim';
  }
  // OTP/PWD branch edges: inactive during passkey flow
  if (branch === 'passkey' && (id === 'e2a' || id === 'e2b' || id === 'e3a' || id === 'e3b')) {
    return 'inactive';
  }
  const f = nodeStates[from];
  const t = nodeStates[to];
  if (f === 'completed' && (t === 'completed' || t === 'current')) return 'active';
  if (f === 'inactive' || t === 'inactive') return 'inactive';
  return 'dim';
}

// -- Style helpers --

function nodeColors(state: NodeState) {
  switch (state) {
    case 'completed':
      return { fill: CYAN, stroke: CYAN, opacity: 1 };
    case 'current':
      return { fill: 'none', stroke: CYAN, opacity: 1 };
    case 'future':
      return { fill: 'none', stroke: CYAN_DIM, opacity: 0.5 };
    case 'inactive':
      return { fill: 'none', stroke: CYAN_GHOST, opacity: 0.15 };
  }
}

function edgeColors(state: EdgeState) {
  switch (state) {
    case 'active':
      return { stroke: CYAN, opacity: 0.8, dash: undefined };
    case 'dim':
      return { stroke: CYAN_DIM, opacity: 0.3, dash: '4 3' };
    case 'inactive':
      return { stroke: CYAN_GHOST, opacity: 0.12, dash: '2 4' };
  }
}

function labelColors(state: NodeState) {
  switch (state) {
    case 'completed':
      return { fill: CYAN, opacity: 0.8 };
    case 'current':
      return { fill: CYAN, opacity: 0.9 };
    case 'future':
      return { fill: CYAN_DIM, opacity: 0.4 };
    case 'inactive':
      return { fill: CYAN_GHOST, opacity: 0.12 };
  }
}

// -- Component --

export const NeuralPath = memo(function NeuralPath({
  currentStep,
  phase,
  sceneExiting = false,
}: NeuralPathProps) {
  const noMotion = useMediaQuery(reducedMotionQuery);

  // Track which branch was selected (persists through success step) — 렌더 중에 조정한다.
  const [branch, setBranch] = useState<'otp' | 'password' | 'passkey' | null>(null);
  const currentBranch =
    currentStep === 'otp'
      ? 'otp'
      : currentStep === 'password'
        ? 'password'
        : currentStep === 'success' && branch === null
          ? 'passkey'
          : branch;
  if (currentBranch !== branch) setBranch(currentBranch);

  const nodeStates = getNodeStates(currentStep, currentBranch);
  const visible = phase >= 1;

  return (
    <div
      className={`${s.container}${sceneExiting ? ` ${s.containerExiting}` : ''}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: noMotion ? undefined : visible ? 'translateY(0)' : 'translateY(10px)',
        transition: noMotion
          ? 'none'
          : 'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDelay: noMotion ? undefined : '0.2s',
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 480 90" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="np-glow">
            <feGaussianBlur stdDeviation="3" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Edges */}
        {EDGES.map((edge) => {
          const state = getEdgeState(edge.id, edge.from, edge.to, nodeStates, currentBranch);
          const ec = edgeColors(state);
          return (
            <g key={edge.id}>
              {state === 'active' && (
                <path d={edge.d} stroke={CYAN_GLOW} strokeWidth="4" opacity="0.3" />
              )}
              <path
                d={edge.d}
                stroke={ec.stroke}
                strokeWidth="1.5"
                opacity={ec.opacity}
                strokeDasharray={ec.dash}
              />
              {state === 'active' && (
                <path
                  d={edge.d}
                  stroke={CYAN}
                  strokeWidth="1.5"
                  strokeDasharray="6 14"
                  className={s.energyFlow}
                  opacity="0.6"
                />
              )}
            </g>
          );
        })}
        {/* Nodes */}
        {NODES.map((node) => {
          const state = nodeStates[node.id] ?? 'future';
          const nc = nodeColors(state);
          const lc = labelColors(state);
          return (
            <g key={node.id}>
              {state === 'current' && (
                <circle
                  cx={node.cx}
                  cy={node.cy}
                  r="8"
                  fill="none"
                  stroke={CYAN}
                  strokeWidth="1"
                  className={s.nodeGlowPulse}
                />
              )}
              {(state === 'completed' || state === 'current') && (
                <circle
                  cx={node.cx}
                  cy={node.cy}
                  r="4"
                  fill={state === 'completed' ? CYAN_GLOW : 'none'}
                  filter="url(#np-glow)"
                />
              )}
              <circle
                cx={node.cx}
                cy={node.cy}
                r="4"
                fill={nc.fill}
                stroke={nc.stroke}
                strokeWidth="1.5"
                opacity={nc.opacity}
              />
              <text
                x={node.cx}
                y={node.cy + node.labelDy}
                textAnchor="middle"
                fill={lc.fill}
                opacity={lc.opacity}
                fontSize="7"
                fontFamily="monospace"
                letterSpacing="1.2"
              >
                {node.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
});
